import * as  ProductActions from './products.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { ActiveFacet, Product, ProductFacet, ProductSort } from '@kion/data-models';

export const PRODUCTS_FEATURE_KEY = 'product';

export interface ProductsState extends EntityState<Product> {
  error?: any;
  lastCreatedId: string | null;
  searchQuery: string[];
  actualSort: string;
  isSearching: boolean;
  textSearchResultIds: string[];
  textSearchTotal: number;
  searchResultIds: string[];
  pagination: {
    page: number,
    perPage: number,
    totalPages: number,
    count: number,
    total: number
  }
  facets: ProductFacet[],
  sorts: ProductSort[],
  referenceIds: string[],
  activeId: string,
  searchTerm: string,
  noResult: boolean
}

export const adapter: EntityAdapter<Product> = createEntityAdapter<Product>();

export interface ProductsPartialState {
  readonly [PRODUCTS_FEATURE_KEY]: ProductsState;
}

export const initialState: ProductsState = adapter.getInitialState({
  lastCreatedId: null,
  isSearching: false,
  textSearchResultIds: [],
  textSearchTotal: 0,
  searchResultIds: [],
  searchQuery: [],
  actualSort: '',
  pagination: {
    page: 1,
    perPage: 20,
    totalPages: 0,
    count: 0,
    total: 0
  },
  facets: [],
  sorts: [],
  activeFacets: [],
  referenceIds: [],
  activeId: null,
  searchTerm: '',
  noResult: false
});

const ProductsReducer = createReducer(
  initialState,
  on(
    ProductActions.loadAllSuccess,
    (state, { products }) => adapter.upsertMany(products, { ...state })
  ),
  on(
    ProductActions.loadProductReferenceSuccess,
    (state, { products }) => adapter.upsertMany(products, { ...state, referenceIds: products.map((product: Product) => product.id) })
  ),
  on(
    ProductActions.setActiveProductSuccess,
    (state, { productId }) => ({ ...state, activeId: productId })
  ),
  on(
    ProductActions.searchError,
    ProductActions.textSearchError,
    ProductActions.search,
    (state) => {
      return { ...state, isSearching: !state.isSearching, noResult: false }
    }
  ),
  on(
    ProductActions.searchSuccess,
    (state, { searchResult } ) => {
      const newState = adapter.upsertMany(
        searchResult.products,
        {
          ...state,
          isSearching: false,
          searchResultIds: searchResult.products.map(
            (product: Product) => product.id
          ),
          pagination: {
            page: searchResult.pagination.currentPage + 1,
            perPage: searchResult.pagination.pageSize,
            totalPages: searchResult.pagination.totalPages,
            total: searchResult.pagination.totalResults
          },
          facets: searchResult.facets,
          sorts: searchResult.sorts,
          actualSort: searchResult.sorts.find((sort: ProductSort) => sort.selected)?.code
        }
      );
      return { ...newState as ProductsState }
    }
  ),
  on(
    ProductActions.textSearchSuccess,
    (state, { textSearchResult }) => {
      return adapter.upsertMany(
        textSearchResult.products,
        {
          ...state,
          isSearching: false,
          textSearchResultIds: textSearchResult.products.map(
            (product: Product) => product.id
          ),
          textSearchTotal: textSearchResult.pagination?.totalResults
        }
      )
    }
  ),
  on(
    ProductActions.loadById,
    (state) => ({ ...state, isSearching: true })
  ),
  on(
    ProductActions.selectProductVariantSuccess,
    ProductActions.loadByIdSuccess,
    (state, { product }) => adapter.upsertOne(product, { ...state, isSearching: false, lastCreatedId: product.id })
  ),
  on(
    ProductActions.loadByIdsSuccess,
    (state, { products }) => adapter.upsertMany(products, { ...state })
  ),
  on(
    ProductActions.updateQuerySuccess,
    (state, { query, clearAll }) => {
      if (clearAll) {
        return {...state, searchQuery: []}
      }
      return { ...state, searchQuery: query }
    }
  ),
  on(
    ProductActions.updatePerPageSuccess,
    ProductActions.updatePageSuccess,
    (state, { pagination }) => {
      return { ...state, pagination: pagination }
    }
  ),
  on(
    ProductActions.updateSortSuccess,
    (state, { sort }) => {
      return {
        ...state, sorts: state.sorts.map(
          s => {
            return {
              ...s,
              selected: s.code === sort
            }
          })
      }
    }
  ),
  on(
    ProductActions.setSearchTerm,
    (state, { searchTerm }) => {
      return { ...state, searchTerm: searchTerm }
    }
  ),
  on(
    ProductActions.setNoResultSuccess,
    (state,action) => {
      return { ...state, noResult: action.toggle }
    }
  ),
);

export function reducer(
  state: ProductsState | undefined,
  action: Action
) {
  return ProductsReducer(state, action);
}
